import * as types from './types';
import { uiActions } from 'state/modules/UI';
import * as api from 'services/api/organizations';
import { getErrorMessage, parsePagination } from 'utils';
import i18n from 'i18n-js';

// GET PARTNERS
const fetchPartnersRequest = () => ({
  type: types.FETCH_PARTNERS_REQUEST,
});

const fetchPartnersSuccess = (payload) => ({
  type: types.FETCH_PARTNERS_SUCCESS,
  payload,
});

const fetchPartnersFailed = (errors) => ({
  type: types.FETCH_PARTNERS_FAILED,
  errors,
});

export const fetchPartners = (params) => {
  return async (dispatch) => {
    dispatch(fetchPartnersRequest());

    const response = await api.fetchOrganizations(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchPartnersSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchPartnersFailed(response.data));
  };
};

// GET PARTNER BY ID
const fetchPartnersByIdRequest = () => ({
  type: types.FETCH_PARTNER_BY_ID_REQUEST,
});

const fetchPartnersByIdSuccess = (payload) => ({
  type: types.FETCH_PARTNER_BY_ID_SUCCESS,
  payload,
});

const fetchPartnersByIdFailed = (errors) => ({
  type: types.FETCH_PARTNER_BY_ID_FAILED,
  errors,
});

export const fetchPartnersById = (id, list) => {
  return async (dispatch) => {
    dispatch(fetchPartnersByIdRequest());

    const response = await api.fetchOrganizationById(id);

    if (response.ok) {
      if (list) {
        return dispatch(
          fetchPartnersSuccess({
            pagination: {},
            data: [response.data],
          })
        );
      }
      return dispatch(
        fetchPartnersByIdSuccess({
          data: response.data,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchPartnersByIdFailed(response.data));
  };
};

// Invite partner
const fetchInvitePartnerRequest = () => ({
  type: types.FETCH_INVITE_PARTNER_REQUEST,
});

const fetchInvitePartnerSuccess = (payload) => ({
  type: types.FETCH_INVITE_PARTNER_SUCCESS,
  payload,
});

const fetchInvitePartnerFailed = (errors) => ({
  type: types.FETCH_INVITE_PARTNER_FAILED,
  errors,
});

export const invitePartner = (data) => {
  return async (dispatch) => {
    dispatch(fetchInvitePartnerRequest());

    const response = await api.invitePartner(data);

    if (response.ok) {
      return dispatch(fetchInvitePartnerSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data, { fullObject: true });

    if (typeof formattedError === 'string') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
      return dispatch(fetchInvitePartnerFailed());
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchInvitePartnerFailed(errors));
  };
};

// Remove partner invite
const removePartnerInviteRequest = () => ({
  type: types.REMOVE_PARTNER_INVITE_REQUEST,
});

const removePartnerInviteSuccess = (payload) => ({
  type: types.REMOVE_PARTNER_INVITE_SUCCESS,
  payload,
});

const removePartnerInviteFailed = (errors) => ({
  type: types.REMOVE_PARTNER_INVITE_FAILED,
  errors,
});

export const removePartnerInvite = (id) => {
  return async (dispatch) => {
    dispatch(removePartnerInviteRequest());

    const response = await api.removePartnerInvite(id);

    if (response.ok) {
      return dispatch(removePartnerInviteSuccess(response.data));
    }

    const formattedError = getErrorMessage(response.data, { fullObject: true });

    if (typeof formattedError === 'string') {
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'error',
          message: formattedError,
        })
      );
      return dispatch(removePartnerInviteFailed());
    }

    let errors = {};

    formattedError?.length &&
      formattedError.map((val) => {
        return (errors[val.field] = val.message);
      });

    dispatch(removePartnerInviteFailed(errors));
  };
};

// GET INVITES
const fetchInvitesRequest = () => ({
  type: types.FETCH_INVITES_REQUEST,
});

const fetchInvitesSuccess = (payload) => ({
  type: types.FETCH_INVITES_SUCCESS,
  payload,
});

const fetchInvitesFailed = (errors) => ({
  type: types.FETCH_INVITES_FAILED,
  errors,
});

export const fetchInvites = (params) => {
  return async (dispatch) => {
    dispatch(fetchInvitesRequest());

    const response = await api.getInvites(params);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      return dispatch(
        fetchInvitesSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(fetchInvitesFailed(response.data));
  };
};

// UPDATE PARTNER STATE
const updatePartnerStatusRequest = () => ({
  type: types.UPDATE_PARTNER_STATE_REQUEST,
});

const updatePartnerStatusSuccess = (payload) => ({
  type: types.UPDATE_PARTNER_STATE_SUCCESS,
  payload,
});

const updatePartnerStatusFailed = (errors) => ({
  type: types.UPDATE_PARTNER_STATE_FAILED,
  errors,
});

export const updatePartnerStatus = (id, status) => {
  return async (dispatch) => {
    dispatch(updatePartnerStatusRequest());

    const response = await api.updatePartnerStatus(id, status);

    if (response.ok) {
      const pagination = parsePagination(response.headers);
      dispatch(
        uiActions.handleShowSnackbar({
          type: 'success',
          message: i18n.t('partners.update_status_success'),
        })
      );
      return dispatch(
        updatePartnerStatusSuccess({
          data: response.data,
          pagination,
        })
      );
    }

    dispatch(
      uiActions.handleShowSnackbar({
        type: 'error',
        message: getErrorMessage(response.data),
      })
    );
    dispatch(updatePartnerStatusFailed(response.data));
  };
};

export const resetPartner = (data) => {
  return async (dispatch) => {
    dispatch({
      type: types.RESET_PARTNER,
    });
  };
};

export const resetPartnersData = () => {
  return async (dispatch) => {
    dispatch({
      type: types.RESET_PARTNERS_DATA,
    });
  };
};
